/**
 * P0 "blueprint mannequin" (15 §5.5, 11 §3.1), V1 silhouette (fidelity pass):
 * every athlete is an ordered stroke list in 11 canonical layers so the un-draw
 * is deterministic. Local athlete space: origin at the feet, x right, y UP
 * (callers negate y for screen). Units are pixels of projected height
 * (`heightPx` = PLAYER_HEIGHT × scale), so the drawing is depth-scaled by
 * construction. Fills are flat two-tone (15 §1.4): each body fill polygon is
 * split at the floodlight azimuth into `fills` (whole polygon, lit tone) and
 * `fills2` (the unlit half, shaded by the painter). No gradients.
 */
import type { PlayerStateName, Team } from '../../../sim/types.js'
import { DASH_TICKS, SHIELD_PERFECT_TICKS, TICK_DT } from '../../../sim/constants.js'
import type { GlyphName } from '../glyphs.js'
import { DASH_IN_MS, DASH_OUT_MS, EXHAUST_MS, FOLLOW_MS, MOVE_SPEED_PER_STRIDE, SHIELD_LOWER_MS, SHIELD_RAISE_MS, WINDUP_MS, lowerFor, upperFor, type ClipState, type LowerClip, type UpperClip } from './clips.js'

export interface Pt { x: number; y: number }
export type Polyline = Pt[]

export type LayerKind = 'ticks' | 'construction' | 'feet' | 'legs' | 'torso' | 'jersey' | 'plate' | 'arms' | 'head' | 'hatch' | 'disc'
export const LAYER_KINDS: readonly LayerKind[] = ['ticks', 'construction', 'feet', 'legs', 'torso', 'jersey', 'plate', 'arms', 'head', 'hatch', 'disc']

export interface Label { text: string; x: number; y: number }
export interface AthleteLayer {
  index: number
  kind: LayerKind
  strokes: Polyline[]
  fills: Polyline[]
  /** Unlit half of each fill (15 §1.4 two-tone step); empty for accent layers and the plate. */
  fills2: Polyline[]
  /** Layer 5 (jersey) and the held disc fill use the team accent. */
  accent: boolean
  labels: Label[]
}
export interface AthleteDrawing {
  layers: AthleteLayer[]
  heightPx: number
  /** Plate quad (for the glyph fit), local space. */
  plate: { cx: number; cy: number; w: number; h: number } | null
  /** Shoulder badge centres (left, right), local space — imprint glyphs go here (12 §2). */
  badges: Pt[]
  /** Jersey polygon (hatch clip). */
  jersey: Polyline
  /** Held disc centre (local), when `held > 0`. */
  discAt: Pt | null
  /** Hand position of the shield arm (for sparks / arc anchoring). */
  shieldHand: Pt | null
  /** Ground shadow ellipse radii (local units × u): follows stance width (15 §2.2). */
  shadow: { rx: number; ry: number }
}

export interface AthletePose {
  state: PlayerStateName
  stateTicks: number
  /** Screen-space unit aim (x right, y down on screen → we use y up here, caller converts). */
  aimScreen: Pt
  velScreen: Pt
  speedFrac: number
  facing: 'back' | 'front'
  heightPx: number
  team: Team
  shield: { age: number; perfect: boolean } | null
  held: number
  hatch: 'diag' | 'dots'
  glyph: GlyphName | null
  /** 12 §2 imprint glyphs (slots 2–3) shown as shoulder badges. */
  badges?: GlyphName[]
  timeMs: number
  local: boolean
  /** Screen-x side the floodlight comes from (−1 = screen-left). Default: team 0 −1, team 1 +1. */
  lightDx?: -1 | 1
  /** 11 §7 resolved clips (renderer); when absent a clip state is synthesised from `state`/`stateTicks`. */
  clips?: ClipState
  /** S10 jump clip (11 §7): `rise`/`fall` tuck (t01 = progress through the half), `land` crouch (t01 through JUMP_LAND_TICKS).
   *  Height comes from the projection, not the drawing — the origin stays at the feet. */
  jump?: { y: number; phase: 'rise' | 'fall' | 'land'; t01: number } | null
}

const clamp01 = (v: number): number => Math.max(0, Math.min(1, v))
const lerp = (a: number, b: number, f: number): number => a + (b - a) * f
const lerpPt = (a: Pt, b: Pt, f: number): Pt => ({ x: lerp(a.x, b.x, f), y: lerp(a.y, b.y, f) })
/** Follow-through: 0 for the first 34 ms (arm fully through), then eases back to 1 by FOLLOW_MS. */
const followFrac = (tc: number): number => clamp01((tc - 34) / (FOLLOW_MS - 34))
type LowerTerms = { strideA: number; strideB: number; bob: number; sway: number }
function blendLower(a: LowerTerms, b: LowerTerms | null, w: number): LowerTerms {
  if (!b) return a
  return { strideA: lerp(b.strideA, a.strideA, w), strideB: lerp(b.strideB, a.strideB, w), bob: lerp(b.bob, a.bob, w), sway: lerp(b.sway, a.sway, w) }
}
/** Blend weight of an upper clip across cur/prev (0 when neither plays it). */
function upperWeight(c: ClipState, clip: UpperClip): number {
  return (c.upper.cur.clip === clip ? c.upper.cur.weight : 0) + (c.upper.prev?.clip === clip ? c.upper.prev.weight : 0)
}
function upperTime(c: ClipState, clip: UpperClip): number {
  return c.upper.cur.clip === clip ? c.upper.cur.t : c.upper.prev?.clip === clip ? c.upper.prev.t : 0
}
/** No resolver upstream (tests, kiosk previews): a settled clip state read straight off the sim state.
 *  Stride phase follows time at the legacy cadence so idle/run/strafe stay distinct per frame. */
function legacyClips(p: AthletePose): ClipState {
  const j = p.jump ?? null
  const inp = { state: p.state, stateTicks: p.stateTicks, speedFrac: p.speedFrac, gaitRad: gaitAngle(p.velScreen, p.aimScreen), alive: true, shieldAge: p.shield?.age ?? null, throwStartedMs: null, nowMs: p.timeMs, grounded: !j || j.phase === 'land', y: j?.y ?? 0, landTicks: j?.phase === 'land' ? 1 : 0 }
  const upper = upperFor(inp)
  const stMs = p.stateTicks * TICK_DT * 1000
  const tUpper = upper === 'shield_hold' ? p.timeMs : upper === 'shield_raise' ? (p.shield?.age ?? p.stateTicks) * TICK_DT * 1000 : stMs
  return {
    lower: { cur: { clip: lowerFor(inp), t: p.timeMs, weight: 1 }, prev: null, stridePhase: (((p.timeMs / 1000) * MOVE_SPEED_PER_STRIDE * Math.max(0.3, p.speedFrac)) % 1 + 1) % 1 },
    upper: { cur: { clip: upper, t: tUpper, weight: 1 }, prev: null },
    whole: p.state === 'DASHING' ? 'dash' : p.state === 'DEREZ' ? 'derez' : j ? 'jump' : 'none',
  }
}

function circle(cx: number, cy: number, r: number, n = 12, rx = r, ry = r, rot = 0): Polyline {
  const out: Polyline = []
  const c = Math.cos(rot), s = Math.sin(rot)
  for (let i = 0; i <= n; i++) { const a = (i / n) * Math.PI * 2; const ex = Math.cos(a) * rx, ey = Math.sin(a) * ry; out.push({ x: cx + ex * c - ey * s, y: cy + ex * s + ey * c }) }
  return out
}

/** Capsule outline between two points with radius r (closed polygon). */
function capsule(a: Pt, b: Pt, r: number): Polyline {
  const dx = b.x - a.x, dy = b.y - a.y
  const out: Polyline = []
  const ang = Math.atan2(dy, dx)
  for (let i = 0; i <= 6; i++) { const t = ang + Math.PI / 2 + (i / 6) * Math.PI; out.push({ x: a.x + Math.cos(t) * r, y: a.y + Math.sin(t) * r }) }
  for (let i = 0; i <= 6; i++) { const t = ang - Math.PI / 2 + (i / 6) * Math.PI; out.push({ x: b.x + Math.cos(t) * r, y: b.y + Math.sin(t) * r }) }
  out.push(out[0])
  return out
}

/** Signed angle between velocity and aim in screen space (radians); 0 = running toward aim. */
export function gaitAngle(vel: Pt, aim: Pt): number {
  if (Math.hypot(vel.x, vel.y) < 1e-6) return 0
  return Math.atan2(vel.x * aim.y - vel.y * aim.x, vel.x * aim.x + vel.y * aim.y)
}

/** 15 §1.4 one hard floodlight step: clip `poly` to the half-plane on the unlit side of the vertical
 *  line `x = splitX` (Sutherland–Hodgman against one edge). Returns [] when nothing is unlit. */
export function unlitHalf(poly: Polyline, splitX: number, lightDx: -1 | 1): Polyline {
  // Unlit side is away from the light: light from the left (−1) leaves x > splitX in shade.
  const inside = (p: Pt): boolean => (lightDx < 0 ? p.x >= splitX : p.x <= splitX)
  const pts = poly.length > 1 && poly[0].x === poly[poly.length - 1].x && poly[0].y === poly[poly.length - 1].y ? poly.slice(0, -1) : poly
  const out: Polyline = []
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length]
    const ia = inside(a), ib = inside(b)
    if (ia) out.push(a)
    if (ia !== ib) { const t = (splitX - a.x) / ((b.x - a.x) || 1e-9); out.push({ x: splitX, y: a.y + (b.y - a.y) * t }) }
  }
  if (out.length < 3) return []
  out.push(out[0])
  return out
}

export function buildAthlete(p: AthletePose): AthleteDrawing {
  const H = p.heightPx
  const u = H / 180 // 1 unit ≈ 1 cm of a 1.80 m athlete
  const front = p.facing === 'front'
  const lightDx: -1 | 1 = p.lightDx ?? (p.team === 0 ? -1 : 1)
  // Horizontal facing sign: aim.x decides which side the throwing arm leads.
  const ax = Math.abs(p.aimScreen.x) < 1e-6 ? 1 : Math.sign(p.aimScreen.x)
  const aimDepth = p.aimScreen.y // + = toward the camera-up direction in our y-up frame

  // ── Clips (11 §7): lower/upper layers with blend weights; legacy poses synthesise one from the sim state ──
  const clips = p.clips ?? legacyClips(p)
  const t = p.timeMs / 1000
  const gait = gaitAngle(p.velScreen, p.aimScreen)
  const moving = clips.lower.cur.clip !== 'idle'
  // Lower body: per-clip stride terms, blended by weight.
  const lowerPose = (clip: LowerClip): { strideA: number; strideB: number; bob: number; sway: number } => {
    const ph = clips.lower.stridePhase * Math.PI * 2
    const amp = 10 + 16 * Math.min(1.2, p.speedFrac)
    switch (clip) {
      case 'run_fwd': return { strideA: Math.sin(ph) * amp, strideB: -Math.sin(ph) * amp, bob: Math.abs(Math.sin(ph)) * 2.5, sway: 0 }
      case 'run_back': return { strideA: -Math.sin(ph) * amp * 0.85, strideB: Math.sin(ph) * amp * 0.85, bob: Math.abs(Math.sin(ph)) * 2.0, sway: 0 }
      case 'strafe_l': return { strideA: Math.sin(ph) * amp * 0.6, strideB: -Math.sin(ph) * amp * 0.6, bob: Math.abs(Math.sin(ph)) * 1.5, sway: Math.cos(ph) * 2 }
      case 'strafe_r': return { strideA: -Math.sin(ph) * amp * 0.6, strideB: Math.sin(ph) * amp * 0.6, bob: Math.abs(Math.sin(ph)) * 1.5, sway: -Math.cos(ph) * 2 }
      default: return { strideA: 0, strideB: 0, bob: Math.sin(t * Math.PI) * 0.8, sway: Math.sin(t * Math.PI) * 1.5 } // 2 s idle sway
    }
  }
  const lo = blendLower(lowerPose(clips.lower.cur.clip), clips.lower.prev ? lowerPose(clips.lower.prev.clip) : null, clips.lower.cur.weight)
  let { strideA, strideB, bob, sway } = lo
  // Dash: whole-body lean into vel, 30 ms in / 60 ms out (DASH_IN_MS / DASH_OUT_MS); legs freeze in a lunge.
  let leanX = 0
  if (clips.whole === 'dash') {
    const stMs = p.stateTicks * TICK_DT * 1000, dashMs = DASH_TICKS * TICK_DT * 1000
    const w = Math.min(1, stMs / DASH_IN_MS, Math.max(0, (dashMs - stMs) / DASH_OUT_MS) + 0.35)
    const f = clamp01(p.stateTicks / DASH_TICKS)
    strideA = 28 * (1 - f); strideB = -22 * (1 - f); bob = 0; sway = 0
    const vl = Math.hypot(p.velScreen.x, p.velScreen.y) || 1
    leanX = (p.velScreen.x / vl) * 14 * w
  }
  // S10 jump (11 §7): `tuck` 0..1 pulls the knees toward the hips and the feet up (rise eases in, fall eases
  // out); `crouch` 0..1 drops the hips on the landing lock. Stride terms are off in the air.
  const jump = clips.whole === 'jump' ? (p.jump ?? { y: 0, phase: 'rise' as const, t01: 0.5 }) : null
  let tuck = 0, crouch = 0
  if (jump) {
    const f = clamp01(jump.t01)
    if (jump.phase === 'rise') tuck = f * f * (3 - 2 * f)
    else if (jump.phase === 'fall') tuck = 1 - f * f * (3 - 2 * f)
    else crouch = Math.sin(Math.PI * f)
    strideA = 0; strideB = 0; bob = 0; sway = 0
  }
  // Exhaust slump: arm drop over 120 ms, torso slump over 1.0 s (11 §5 / §7), weighted by the clip blend.
  const exW = upperWeight(clips, 'exhaust')
  const exT = upperTime(clips, 'exhaust')
  const slump = exW * 10 * clamp01(exT / EXHAUST_MS)

  // ── Proportions (15 §1 / spec V1): head 1/7.5, shoulders 0.26 H, hips 0.17 H ──
  const headR = 12
  const shoulderW = 48
  const hipW = 30
  // Tuck: feet up 28, knees up 24 and forward 10 (thigh foreshortens toward the camera, shin stays ≈ 36 long); land: hips drop 14, knees bend and splay.
  const hipY = 92 - bob - slump * 0.3 - 14 * crouch
  const kneeY = 48 + 24 * tuck - 6 * crouch
  const footY = 28 * tuck
  const footSpread = 11 + 4 * Math.abs(Math.sin(gait)) * Math.min(1, p.speedFrac) * (moving && !jump ? 1 : 0) + 4 * crouch
  const lFoot: Pt = { x: -footSpread + strideA * 0.08 * ax, y: footY }
  const rFoot: Pt = { x: footSpread + strideB * 0.08 * ax, y: footY }
  // Strides show as knee lift (y) + fore-aft (x) offsets; the tuck pulls the knees forward (toward aim) and the land crouch splays them.
  const lKnee: Pt = { x: -hipW * 0.3 + strideA * 0.25 + 10 * tuck * ax - 7 * crouch, y: kneeY + Math.max(0, strideA) * 0.35 }
  const rKnee: Pt = { x: hipW * 0.3 + strideB * 0.25 + 10 * tuck * ax + 7 * crouch, y: kneeY + Math.max(0, strideB) * 0.35 }
  const lHip: Pt = { x: -hipW * 0.5 + sway + leanX * 0.4, y: hipY }
  const rHip: Pt = { x: hipW * 0.5 + sway + leanX * 0.4, y: hipY }
  const lAnkle: Pt = { x: lFoot.x, y: footY + 8 }, rAnkle: Pt = { x: rFoot.x, y: footY + 8 }

  // ── Upper body ──────────────────────────────────────────────────────────
  const shoulderY = 142 - bob - slump - 14 * crouch
  const cx = sway + leanX
  // Upper-body yaw (blended): windup rotates 20° away from aim over 50 ms; follow-through swings 12° back.
  const yawFor = (clip: UpperClip, tc: number): number => {
    if (clip === 'windup') return -20 * (Math.PI / 180) * ax * clamp01(tc / WINDUP_MS)
    if (clip === 'follow') return 12 * (Math.PI / 180) * ax * (1 - followFrac(tc))
    return 0
  }
  const yaw = clips.whole === 'dash' ? 0 : yawFor(clips.upper.cur.clip, clips.upper.cur.t) * clips.upper.cur.weight + (clips.upper.prev ? yawFor(clips.upper.prev.clip, clips.upper.prev.t) * clips.upper.prev.weight : 0)
  const yawScale = Math.cos(yaw) // apparent shoulder width shrink
  const lSh: Pt = { x: cx - shoulderW * 0.5 * yawScale, y: shoulderY }
  const rSh: Pt = { x: cx + shoulderW * 0.5 * yawScale, y: shoulderY }
  const neck: Pt = { x: cx, y: shoulderY + 4 }
  const headC: Pt = { x: cx + leanX * 0.2, y: 168 - bob - slump * 1.2 - 14 * crouch }

  // Throwing arm (aim side) and off arm.
  const throwSide = ax
  const shThrow: Pt = throwSide > 0 ? rSh : lSh
  const shOff: Pt = throwSide > 0 ? lSh : rSh
  const armLen = 30
  const armDirX = p.aimScreen.x, armDirY = aimDepth
  const al = Math.hypot(armDirX, armDirY) || 1
  const adx = armDirX / al, ady = armDirY / al
  interface Arms { elbowThrow: Pt; handThrow: Pt; elbowOff: Pt; handOff: Pt }
  const idleArms = (): Arms => {
    if (tuck > 0) {
      // Airborne: arms out for balance (elbows at shoulder height, hands a little above), disc hand still leads.
      const out = 14 + 18 * tuck
      return {
        elbowThrow: { x: shThrow.x + throwSide * out * 0.55, y: shoulderY }, handThrow: { x: shThrow.x + throwSide * out, y: shoulderY + 4 * tuck },
        elbowOff: { x: shOff.x - throwSide * out * 0.55, y: shoulderY }, handOff: { x: shOff.x - throwSide * out, y: shoulderY + 4 * tuck },
      }
    }
    // Idle/moving arms: disc hand at the hip, off arm swinging with gait.
    const swing = moving && !jump ? strideB * 0.3 : Math.sin(t * Math.PI) * 1
    return {
      elbowThrow: { x: shThrow.x + throwSide * 6, y: shoulderY - 16 }, handThrow: { x: shThrow.x + throwSide * 8, y: hipY + 6 },
      elbowOff: { x: shOff.x - throwSide * 6 + swing * 0.3, y: shoulderY - 16 }, handOff: { x: shOff.x - throwSide * 7 + swing * 0.6, y: hipY + 4 + Math.abs(swing) * 0.2 },
    }
  }
  const armsFor = (clip: UpperClip, tc: number): Arms => {
    switch (clip) {
      case 'windup': {
        const f = clamp01(tc / WINDUP_MS)
        // Arm draws back 12 units over 50 ms (opposite aim), disc hand behind the hip.
        return {
          elbowThrow: { x: shThrow.x + throwSide * 10, y: shoulderY - 14 }, handThrow: { x: shThrow.x + throwSide * (16 + 12 * f) - adx * 6, y: hipY + 14 - 6 * f },
          elbowOff: { x: shOff.x - throwSide * 10, y: shoulderY - 4 }, handOff: { x: shOff.x - throwSide * 16 + adx * 8, y: shoulderY + 6 },
        }
      }
      case 'follow': {
        // Release snaps the arm through (first 34 ms fully extended), then eases back over the rest.
        const ext = lerp(1, 0.2, followFrac(tc))
        return {
          elbowThrow: { x: shThrow.x + adx * 14 * ext, y: shoulderY + ady * 8 * ext - 4 * (1 - ext) }, handThrow: { x: shThrow.x + adx * armLen * ext + throwSide * 4 * (1 - ext), y: shoulderY + ady * 16 * ext - 26 * (1 - ext) },
          elbowOff: { x: shOff.x - throwSide * 8, y: shoulderY - 10 }, handOff: { x: shOff.x - throwSide * 6, y: shoulderY - 24 },
        }
      }
      case 'shield_raise': case 'shield_hold': case 'shield_lower': {
        const raise = clip === 'shield_raise' ? clamp01(tc / SHIELD_RAISE_MS) : clip === 'shield_lower' ? 1 - clamp01(tc / SHIELD_LOWER_MS) : 1
        const breathe = clip === 'shield_hold' ? Math.sin((tc / 1500) * Math.PI * 2) * 1.5 : 0
        // Shield (off) arm up and forward along aim; throw arm tucked.
        return {
          elbowOff: { x: shOff.x + adx * 10, y: shoulderY - 6 + 8 * raise }, handOff: { x: shOff.x + adx * 24 * raise, y: shoulderY - 14 + 22 * raise + breathe },
          elbowThrow: { x: shThrow.x + throwSide * 8, y: shoulderY - 12 }, handThrow: { x: shThrow.x + throwSide * 6, y: hipY + 4 },
        }
      }
      case 'exhaust': {
        // Arms drop over 120 ms, then everything hangs with the slump.
        const drop = clamp01(tc / 120), sl = clamp01(tc / EXHAUST_MS) * 6
        const idle = idleArms()
        return {
          elbowOff: { x: shOff.x - throwSide * 6, y: shoulderY - 14 }, handOff: lerpPt(idle.handOff, { x: shOff.x - throwSide * 4, y: hipY - 2 - sl }, drop),
          elbowThrow: { x: shThrow.x + throwSide * 6, y: shoulderY - 14 }, handThrow: lerpPt(idle.handThrow, { x: shThrow.x + throwSide * 4, y: hipY - 2 - sl }, drop),
        }
      }
      default: return idleArms()
    }
  }
  let armPose: Arms
  if (clips.whole === 'dash') {
    armPose = {
      elbowThrow: { x: shThrow.x + leanX * 0.6 - throwSide * 4, y: shoulderY - 12 }, handThrow: { x: shThrow.x + leanX * 1.2 - throwSide * 10, y: shoulderY - 2 },
      elbowOff: { x: shOff.x + leanX * 0.6 + throwSide * 4, y: shoulderY - 12 }, handOff: { x: shOff.x + leanX * 1.2 + throwSide * 10, y: shoulderY - 2 },
    }
  } else {
    const a = armsFor(clips.upper.cur.clip, clips.upper.cur.t)
    const b = clips.upper.prev ? armsFor(clips.upper.prev.clip, clips.upper.prev.t) : null
    const w = clips.upper.cur.weight
    armPose = b ? { elbowThrow: lerpPt(b.elbowThrow, a.elbowThrow, w), handThrow: lerpPt(b.handThrow, a.handThrow, w), elbowOff: lerpPt(b.elbowOff, a.elbowOff, w), handOff: lerpPt(b.handOff, a.handOff, w) } : a
  }
  const { elbowThrow, handThrow, elbowOff, handOff } = armPose
  /** Blunt glove: a short capsule continuing the forearm 6 units past the wrist. */
  const glove = (elbow: Pt, hand: Pt): Polyline => { const dx = hand.x - elbow.x, dy = hand.y - elbow.y, l = Math.hypot(dx, dy) || 1; return capsule(hand, { x: hand.x + (dx / l) * 6, y: hand.y + (dy / l) * 6 }, 4.5) }
  /** Boot: blunt capsule sitting on y = 0, toe toward aim. */
  const boot = (foot: Pt): Polyline => capsule({ x: foot.x - 3 * ax, y: foot.y + 4.5 }, { x: foot.x + 5 * ax, y: foot.y + 4.5 }, 4.5)

  // ── Layers ──────────────────────────────────────────────────────────────
  const hipOutL: Pt = { x: lHip.x - 2, y: lHip.y - 2 }, hipOutR: Pt = { x: rHip.x + 2, y: rHip.y - 2 }
  const torsoPoly: Polyline = [lSh, rSh, hipOutR, hipOutL, lSh]
  // Jersey block: upper 60 % of the torso.
  const jy = lerp(hipY, shoulderY, 0.4)
  const jersey: Polyline = [
    { x: lSh.x, y: lSh.y }, { x: rSh.x, y: rSh.y },
    { x: lerp(hipOutR.x, rSh.x, 0.4), y: jy }, { x: lerp(hipOutL.x, lSh.x, 0.4), y: jy }, { x: lSh.x, y: lSh.y },
  ]
  const plateW = 28 * Math.max(0.25, yawScale), plateH = 24
  const plateC: Pt = { x: cx + Math.sin(yaw) * 4, y: lerp(hipY, shoulderY, 0.62) }
  const plateQuad: Polyline = [
    { x: plateC.x - plateW / 2, y: plateC.y + plateH / 2 }, { x: plateC.x + plateW / 2, y: plateC.y + plateH / 2 },
    { x: plateC.x + plateW / 2, y: plateC.y - plateH / 2 }, { x: plateC.x - plateW / 2, y: plateC.y - plateH / 2 },
    { x: plateC.x - plateW / 2, y: plateC.y + plateH / 2 },
  ]

  const ticks: AthleteLayer = {
    index: 0, kind: 'ticks', accent: false, fills: [], fills2: [],
    strokes: [
      // Height tick: vertical dimension line beside the athlete with end ticks.
      [{ x: 34, y: 0 }, { x: 34, y: 180 }], [{ x: 30, y: 0 }, { x: 38, y: 0 }], [{ x: 30, y: 180 }, { x: 38, y: 180 }],
      // Stance width tick under the feet.
      [{ x: lFoot.x, y: -6 }, { x: rFoot.x, y: -6 }], [{ x: lFoot.x, y: -9 }, { x: lFoot.x, y: -3 }], [{ x: rFoot.x, y: -9 }, { x: rFoot.x, y: -3 }],
      // Centre line.
      [{ x: cx, y: -4 }, { x: cx, y: 184 }],
    ],
    labels: [{ text: '1.80', x: 40, y: 92 }, { text: (Math.abs(rFoot.x - lFoot.x) / 100).toFixed(2), x: 0, y: -14 }],
  }
  const leftArm: Polyline = throwSide > 0 ? [lSh, elbowOff, handOff] : [lSh, elbowThrow, handThrow]
  const rightArm: Polyline = throwSide > 0 ? [rSh, elbowThrow, handThrow] : [rSh, elbowOff, handOff]
  const construction: AthleteLayer = {
    index: 1, kind: 'construction', accent: false, fills: [], fills2: [],
    strokes: [
      circle(cx, hipY, hipW * 0.55, 10, hipW * 0.55, hipW * 0.22),
      circle(cx, shoulderY, shoulderW * 0.5, 10, shoulderW * 0.5, shoulderW * 0.18),
      [{ x: cx, y: hipY }, { x: neck.x, y: neck.y }],
      [lHip, lKnee, lFoot], [rHip, rKnee, rFoot], leftArm, rightArm,
    ],
    labels: [],
  }
  // Layer 2: shins + boots (boots sit on y = 0; a ground contact tick under each).
  const shinL = capsule(lKnee, lAnkle, 4.0), shinR = capsule(rKnee, rAnkle, 4.0)
  const bootL = boot(lFoot), bootR = boot(rFoot)
  const feet: AthleteLayer = {
    index: 2, kind: 'feet', accent: false,
    strokes: [shinL, shinR, bootL, bootR, [{ x: lFoot.x - 6, y: footY }, { x: lFoot.x + 6, y: footY }], [{ x: rFoot.x - 6, y: footY }, { x: rFoot.x + 6, y: footY }]],
    fills: [shinL, shinR, bootL, bootR], fills2: [], labels: [],
  }
  // Layer 3: thighs + pelvis, knee joints as short overlapping capsules.
  const thighL = capsule(lHip, lKnee, 5.0), thighR = capsule(rHip, rKnee, 5.0)
  const pelvis: Polyline = [{ x: lHip.x - 3, y: hipY + 2 }, { x: rHip.x + 3, y: hipY + 2 }, { x: rHip.x + 1, y: hipY - 8 }, { x: lHip.x - 1, y: hipY - 8 }, { x: lHip.x - 3, y: hipY + 2 }]
  const legs: AthleteLayer = {
    index: 3, kind: 'legs', accent: false,
    strokes: [thighL, thighR, circle(lKnee.x, lKnee.y, 4.5, 8), circle(rKnee.x, rKnee.y, 4.5, 8)],
    fills: [pelvis, thighL, thighR], fills2: [], labels: [],
  }
  const torso: AthleteLayer = { index: 4, kind: 'torso', accent: false, strokes: [torsoPoly], fills: [torsoPoly], fills2: [], labels: [] }
  if (!front) {
    // Back view: shoulder-pad seams instead of a face (spec V1).
    torso.strokes.push([{ x: lSh.x + 4, y: lSh.y - 2 }, { x: lSh.x + 9, y: lSh.y - 10 }], [{ x: rSh.x - 4, y: rSh.y - 2 }, { x: rSh.x - 9, y: rSh.y - 10 }])
  }
  const jerseyL: AthleteLayer = { index: 5, kind: 'jersey', accent: true, strokes: [], fills: [jersey], fills2: [], labels: [] }
  const plate: AthleteLayer = { index: 6, kind: 'plate', accent: false, strokes: [plateQuad], fills: [plateQuad], fills2: [], labels: [] }
  // Shoulder badges (team glyph) — small squares/circles on the shoulders.
  const badgeL = p.team === 0 ? [{ x: lSh.x - 3, y: lSh.y - 3 }, { x: lSh.x + 3, y: lSh.y - 3 }, { x: lSh.x + 3, y: lSh.y + 3 }, { x: lSh.x - 3, y: lSh.y + 3 }, { x: lSh.x - 3, y: lSh.y - 3 }] : circle(lSh.x, lSh.y, 3, 8)
  const badgeR = p.team === 0 ? [{ x: rSh.x - 3, y: rSh.y - 3 }, { x: rSh.x + 3, y: rSh.y - 3 }, { x: rSh.x + 3, y: rSh.y + 3 }, { x: rSh.x - 3, y: rSh.y + 3 }, { x: rSh.x - 3, y: rSh.y - 3 }] : circle(rSh.x, rSh.y, 3, 8)
  plate.strokes.push(badgeL, badgeR)
  // Layer 7: arms; the last two strokes are the gloves (throw hand first) — tests and sparks anchor on them.
  const upperT = capsule(shThrow, elbowThrow, 4.2), foreT = capsule(elbowThrow, handThrow, 3.6)
  const upperO = capsule(shOff, elbowOff, 4.2), foreO = capsule(elbowOff, handOff, 3.6)
  const gloveT = glove(elbowThrow, handThrow), gloveO = glove(elbowOff, handOff)
  const arms: AthleteLayer = {
    index: 7, kind: 'arms', accent: false,
    strokes: [upperT, foreT, upperO, foreO, gloveT, gloveO],
    fills: [upperT, foreT, upperO, foreO, gloveT, gloveO], fills2: [], labels: [],
  }
  // Head: outline + neck; front shows eyes, brow and a jaw notch; back shows nothing (PHASE1 visible face).
  const headStrokes: Polyline[] = [circle(headC.x, headC.y, headR, 16), [{ x: neck.x, y: neck.y }, { x: headC.x, y: headC.y - headR }]]
  if (front) {
    const ex = 4 * ax * 0.6 + Math.sin(yaw) * 3
    headStrokes.push([{ x: headC.x - 6 + ex, y: headC.y + 1 }, { x: headC.x - 2 + ex, y: headC.y + 1 }], [{ x: headC.x + 2 + ex, y: headC.y + 1 }, { x: headC.x + 6 + ex, y: headC.y + 1 }])
    headStrokes.push([{ x: headC.x - 7 + ex, y: headC.y + 5.5 }, { x: headC.x + 7 + ex, y: headC.y + 5.5 }])
    headStrokes.push([{ x: headC.x - 4, y: headC.y - 8 }, { x: headC.x, y: headC.y - 10 }, { x: headC.x + 4, y: headC.y - 8 }])
  }
  const head: AthleteLayer = { index: 8, kind: 'head', accent: false, strokes: headStrokes, fills: [circle(headC.x, headC.y, headR, 16)], fills2: [], labels: [] }
  // Hatch: generated as strokes clipped to the jersey by the renderer; we provide the pattern lines within the jersey bbox.
  const hatch: AthleteLayer = { index: 9, kind: 'hatch', accent: false, strokes: [], fills: [], fills2: [], labels: [] }
  {
    const xs = jersey.map((q) => q.x), ys = jersey.map((q) => q.y)
    const x0 = Math.min(...xs), x1 = Math.max(...xs), y0 = Math.min(...ys), y1 = Math.max(...ys)
    const pitch = Math.max(3, 3 / Math.max(0.2, u * 1.0)) // ≈3 px on screen
    if (p.hatch === 'diag') {
      for (let s = x0 - (y1 - y0); s < x1; s += pitch) hatch.strokes.push([{ x: s, y: y0 }, { x: s + (y1 - y0), y: y1 }])
    } else {
      for (let yy = y0 + pitch / 2; yy < y1; yy += pitch) for (let xx = x0 + pitch / 2; xx < x1; xx += pitch) hatch.strokes.push([{ x: xx, y: yy }, { x: xx + 0.6, y: yy }])
    }
  }
  // Held disc: in the throw hand, ring tilted with the forearm (flat when the arm hangs).
  const discR = 22 // DISC_RADIUS 0.22 m in cm units, 0.45 y-squash (15 §2.3)
  const discAt: Pt | null = p.held > 0 ? { x: handThrow.x + throwSide * discR * 0.3, y: handThrow.y } : null
  const foreAng = Math.atan2(handThrow.y - elbowThrow.y, handThrow.x - elbowThrow.x)
  let tilt = (foreAng + Math.PI / 2) * 0.5; while (tilt > Math.PI / 2) tilt -= Math.PI; while (tilt < -Math.PI / 2) tilt += Math.PI
  const ring = (c: Pt): Polyline => circle(c.x, c.y, discR, 16, discR, discR * 0.45, tilt)
  const disc: AthleteLayer = {
    index: 10, kind: 'disc', accent: true,
    strokes: discAt ? [ring(discAt)] : [],
    fills: discAt ? [ring(discAt)] : [], fills2: [], labels: [],
  }
  if (discAt && p.held > 1) disc.strokes.push(ring({ x: discAt.x - throwSide * 3, y: discAt.y + 3 }))

  // Two-tone: body layers get an unlit half per fill (split 3 units off-centre, toward the shade).
  const splitX = cx - lightDx * 3
  for (const L of [feet, legs, torso, arms, head]) L.fills2 = L.fills.map((f) => unlitHalf(f, splitX, lightDx)).filter((f) => f.length > 0)

  const layers = [ticks, construction, feet, legs, torso, jerseyL, plate, arms, head, hatch, disc]
  // Scale everything to pixels.
  const S = (pl: Polyline): Polyline => pl.map((q) => ({ x: q.x * u, y: q.y * u }))
  for (const L of layers) {
    L.strokes = L.strokes.map(S)
    L.fills = L.fills.map(S)
    L.fills2 = L.fills2.map(S)
    L.labels = L.labels.map((l) => ({ text: l.text, x: l.x * u, y: l.y * u }))
  }
  const shadowRx = footSpread + 16
  return {
    layers, heightPx: H,
    plate: { cx: plateC.x * u, cy: plateC.y * u, w: plateW * u, h: plateH * u },
    badges: [{ x: lSh.x * u, y: lSh.y * u }, { x: rSh.x * u, y: rSh.y * u }],
    jersey: S(jersey),
    discAt: discAt ? { x: discAt.x * u, y: discAt.y * u } : null,
    shieldHand: p.state === 'SHIELD' ? { x: handOff.x * u, y: handOff.y * u } : null,
    shadow: { rx: shadowRx * u, ry: shadowRx * 0.38 * u },
  }
}

/** Cheap structural hash of a drawing (tests: state-distinctness). */
export function drawingHash(d: AthleteDrawing): number {
  let h = 0x811c9dc5
  const mix = (v: number) => { h ^= Math.round(v * 10) & 0xffff; h = Math.imul(h, 0x01000193) >>> 0 }
  for (const L of d.layers) { mix(L.index); for (const s of L.strokes) for (const q of s) { mix(q.x); mix(q.y) } for (const f of L.fills) for (const q of f) { mix(q.x); mix(q.y) } }
  return h >>> 0
}

export const SECONDS_PER_TICK = TICK_DT
export const PERFECT_TICKS = SHIELD_PERFECT_TICKS
